import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Same `{data, loading, error, refresh}` polling primitive that the
 * interior + video sub-apps use. Duplicated inside the ads subtree to
 * keep the dependency direction clean (a sub-app's components
 * shouldn't reach into another sub-app's hooks), at the cost of a few
 * lines of repetition.
 */

export interface PollingResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function usePolling<T>(
  fetcher: () => Promise<T>,
  intervalMs: number,
  deps: ReadonlyArray<unknown> = [],
): PollingResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef(false);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const tick = useCallback(async () => {
    try {
      const result = await fetcherRef.current();
      if (cancelledRef.current) return;
      setData(result);
      setError(null);
    } catch (ex) {
      if (cancelledRef.current) return;
      setError(ex instanceof Error ? ex.message : 'Unknown error');
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    setLoading(true);
    tick();
    const id = window.setInterval(tick, intervalMs);
    return () => {
      cancelledRef.current = true;
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, tick, ...deps]);

  return { data, loading, error, refresh: tick };
}
